import { ErrorIcon } from '@assets/icons';
import { Form, FormTextInput, StandardFormButtons } from '@components/form';
import { FormFieldError } from '@components/form/FormInputs/FormFieldError';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import {
  useEditEditorMutation,
  useGetAvailableInstanceTypesWithDetailsQuery,
  type Editor,
  type InstanceTypeDetails,
} from '@generated/graphql';
import { Box, Stack, useTheme } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { useFormikContext } from 'formik';
import posthog from 'posthog-js';
import { useMemo, useState } from 'react';
import * as yup from 'yup';
import { AdvancedSettingsSection } from './AdvancedSettingsSection';
import { InstanceSelector } from './InstanceSelector';

const createValidationSchema = (isStopped: boolean, originalInstanceType: string) =>
  yup.object({
    name: yup.string().trim().required('Name is required'),
    instanceType: yup
      .string()
      .test(
        'stopped-for-instance-change',
        'Server must be stopped to change instance type',
        (value) => {
          const newType = value || '';
          if (!newType || newType === originalInstanceType) return true;
          return isStopped;
        },
      ),
  });

const InstanceTypeFieldError = ({
  originalInstanceType,
  isStopped,
}: {
  originalInstanceType: string;
  isStopped: boolean;
}) => {
  const { values } = useFormikContext<{ instanceType: { value: string } }>();
  const newType = values.instanceType?.value || '';
  const changed = !!newType && newType !== originalInstanceType;

  if (!changed || isStopped) return null;

  return <FormFieldError error="Server must be stopped to change instance type" maintainGutters />;
};

const CrossFamilyWarning = ({
  originalInstanceType,
  instanceTypes,
  isStopped,
}: {
  originalInstanceType: string;
  instanceTypes: InstanceTypeDetails[];
  isStopped: boolean;
}) => {
  const { palette } = useTheme();
  const { values } = useFormikContext<{ instanceType: { value: string } }>();
  const newInstanceType = values.instanceType?.value || '';

  if (!isStopped) return null;
  if (!newInstanceType || newInstanceType === originalInstanceType) return null;

  const originalTypeInfo = instanceTypes.find((t) => t.instanceType === originalInstanceType);
  const newTypeInfo = instanceTypes.find((t) => t.instanceType === newInstanceType);

  if (!originalTypeInfo || !newTypeInfo) return null;

  const wasGpu = originalTypeInfo.gpuAvailable;
  const isGpu = newTypeInfo.gpuAvailable;

  if (wasGpu === isGpu) return null;

  const isCpuToGpu = !wasGpu && isGpu;

  return (
    <Box
      sx={{
        border: `1px solid ${palette.warning.main}`,
        borderRadius: 1,
        px: 2,
        py: 2,
        mb: 5,
      }}
    >
      <Group gap={2} alignItems="flex-start">
        <ErrorIcon
          size={14}
          color={palette.warning.main}
          style={{ marginTop: '2px', flexShrink: 0 }}
        />
        <Stack gap={1}>
          {isCpuToGpu ? (
            <>
              <Typography variant="caption1" sx={{ color: 'warning.main', fontWeight: 600 }}>
                GPU may not work correctly
              </Typography>
              <Typography variant="caption2" sx={{ color: 'text.paper' }}>
                Switching to a GPU instance type on an existing server may cause driver issues — GPU
                workloads may not run as expected. For the best experience, create a new server and
                select a GPU instance type from the start.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="caption1" sx={{ color: 'warning.main', fontWeight: 600 }}>
                Switching to a CPU instance type
              </Typography>
              <Typography variant="caption2" sx={{ color: 'text.paper' }}>
                Your server will run on CPU hardware. GPU-specific software may still be present but
                won&apos;t be usable. For a clean CPU environment, create a new server and select a
                CPU instance type from the start.
              </Typography>
            </>
          )}
        </Stack>
      </Group>
    </Box>
  );
};

type EditServerModalProps = {
  server: Pick<
    Editor,
    | 'editorId'
    | 'name'
    | 'instanceType'
    | 'status'
    | 'autoStopEnabled'
    | 'autoStopCpuThreshold'
    | 'autoStopTimeoutMinutes'
    | 'autoDeleteEnabled'
    | 'autoDeleteTimeoutDays'
    | 'autoUpdateEnabled'
  >;
  onEdit: () => void;
  onClose: () => void;
};

export const EditServerModal = ({ server, onEdit, onClose }: EditServerModalProps) => {
  const { createAsyncNotification } = useNotifications();

  const [editServerMutation] = useEditEditorMutation();
  const [formError, setFormError] = useState<string | null>(null);

  const { data: instanceTypesData } = useGetAvailableInstanceTypesWithDetailsQuery({
    variables: { filters: {} },
  });
  const instanceTypes = instanceTypesData?.getInstanceTypesWithDetails || [];

  const isStopped = server.status?.state === 'stopped';
  const validationSchema = useMemo(
    () => createValidationSchema(isStopped, server.instanceType || ''),
    [isStopped, server.instanceType],
  );

  const submitServerEdit = async ({
    name,
    instanceType: instanceTypeField,
    autoStopEnabled,
    autoStopCpuThreshold,
    autoStopTimeoutMinutes,
    autoDeleteEnabled,
    autoDeleteTimeoutDays,
    autoUpdateEnabled,
  }: {
    name: string;
    instanceType: string;
    autoStopEnabled: boolean;
    autoStopCpuThreshold: number;
    autoStopTimeoutMinutes: string;
    autoDeleteEnabled: boolean;
    autoDeleteTimeoutDays: number;
    autoUpdateEnabled: boolean;
  }) => {
    await editServerMutation({
      variables: {
        editorId: server.editorId,
        name,
        instanceType: instanceTypeField !== server.instanceType ? instanceTypeField : undefined,
        autoStopEnabled,
        autoStopCpuThreshold: autoStopEnabled ? autoStopCpuThreshold : undefined,
        autoStopTimeoutMinutes: autoStopEnabled ? parseInt(autoStopTimeoutMinutes, 10) : undefined,
        autoDeleteEnabled,
        autoDeleteTimeoutDays: autoDeleteEnabled ? autoDeleteTimeoutDays : undefined,
        autoUpdateEnabled,
      },
      onCompleted: () => {
        posthog.capture('services:user_edit-server', {
          editorId: server.editorId,
          name,
          instanceType: instanceTypeField,
        });
        onEdit?.();
        onClose();
      },
      onError: (error) => {
        setFormError(error.message);
        throw error;
      },
    });
  };

  const handleFormSubmit = createAsyncNotification(
    'Server updated successfully',
    submitServerEdit,
    {
      loadingMessage: 'Updating server...',
    },
  );

  return (
    <Form
      initialValues={{
        name: server.name || '',
        instanceType: server.instanceType || '',
        autoStopEnabled: server.autoStopEnabled ?? true,
        autoStopCpuThreshold: server.autoStopCpuThreshold ?? 20,
        autoStopTimeoutMinutes: server.autoStopTimeoutMinutes ?? 180,
        autoDeleteEnabled: server.autoDeleteEnabled ?? true,
        autoDeleteTimeoutDays: server.autoDeleteTimeoutDays ?? 14,
        autoUpdateEnabled: server.autoUpdateEnabled ?? true,
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {() => (
        <Stack style={{ maxWidth: '378px' }}>
          <FormTextInput
            name="name"
            label="Name"
            labelInfo="An optional name to identify this server."
          />
          <InstanceSelector
            name="instanceType"
            label="Instance type"
            labelInfo="The hardware configuration for this server. Choose GPU instances for AI/ML workloads."
          />
          <InstanceTypeFieldError
            originalInstanceType={server.instanceType || ''}
            isStopped={isStopped}
          />
          <CrossFamilyWarning
            originalInstanceType={server.instanceType || ''}
            instanceTypes={instanceTypes}
            isStopped={isStopped}
          />

          <AdvancedSettingsSection />

          <StandardFormButtons onClose={onClose} formError={formError} submitLabel="Save" />
        </Stack>
      )}
    </Form>
  );
};
